import { useAuth } from "@clerk/expo";
import { useCallback, useState } from "react";

import { useAxiosClient, useLocalization } from "@/hooks";
import { SuggestQuestionsRequest, SuggestStudyRequest } from "@/types/requests";
import {
  ErrorResponse,
  QuestionSuggestion,
  StudySuggestion,
  SuggestQuestionsResponse,
  SuggestStudyResponse,
} from "@/types/responses";

const useSuggestQuestions = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/ai/suggest");

  const suggestQuestions = useCallback(
    async (
      data: SuggestQuestionsRequest
    ): Promise<QuestionSuggestion | null> => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.post<SuggestQuestionsResponse>(
          "/questions",
          data
        );
        return response.data.suggestion;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    error,
    loading,
    suggestQuestions,
  };
};

/**
 * The study design outlives the router's first-byte timeout, so the API
 * streams NDJSON: heartbeat blank lines, then one JSON line holding either the
 * suggestion or an in-band error. `fetch` buffers the whole body; we only need
 * the last line.
 */
const useSuggestStudy = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { getToken } = useAuth();
  const { i18n, locale } = useLocalization();

  const suggestStudy = useCallback(
    async (
      data: SuggestStudyRequest,
      signal?: AbortSignal
    ): Promise<StudySuggestion | null> => {
      setError(null);
      setLoading(true);

      try {
        const response = await fetch(
          `${process.env.EXPO_PUBLIC_API_URL}/ai/suggest/studies`,
          {
            body: JSON.stringify(data),
            headers: {
              "Accept-Language": locale.replace("-", "_"),
              Authorization: `Bearer ${await getToken()}`,
              "Content-Type": "application/json",
            },
            method: "POST",
            signal,
          }
        );
        const lastLine = (await response.text()).trim().split("\n").pop();
        let payload: SuggestStudyResponse | ErrorResponse | null = null;
        try {
          payload = JSON.parse(lastLine || "null");
        } catch {
          // Not our NDJSON: a cut connection or a proxy/router error page.
        }
        if (!response.ok || payload === null || "error" in payload) {
          throw new Error(payload?.message || i18n.t("misc.errorOccurred"));
        }
        return payload;
      } catch (e) {
        // The caller gave up on the design: not an error to show.
        if (signal?.aborted) return null;
        const message = e instanceof Error ? e.message : String(e);
        console.error(message);
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getToken, i18n, locale]
  );

  return {
    error,
    loading,
    suggestStudy,
  };
};

export { useSuggestQuestions, useSuggestStudy };
